import Axios, { AxiosResponse } from 'axios';
import { observable, computed, action, toJS } from 'mobx';
import { mergeRight } from 'ramda';

import Services from '@services/index';
import { JrpcResponse } from '@httpClient/jrpc';
import {
  ModuleInterface,
  ModuleKeys,
  ModuleFilterProps,
  LoadModule,
  Module,
} from '@core/entities/Opencity/Module';
import Store from './Store';
import { Loading, endLoading } from './interfaces/Loading';
import { Pagination } from './interfaces/Pagination';
import { Filter } from './interfaces/Filter';
import { Entity } from './interfaces/Entity';

type ModuleIndexResponse = JrpcResponse<
  { items: ModuleInterface[]; total: number },
  {},
  { filter: ModuleFilterProps }
>;

class ModuleStore extends Store
  implements
    Loading,
    Pagination,
    Filter<ModuleFilterProps, ModuleKeys>,
    Entity<Module, { filter?: ModuleFilterProps }, number, Module, ModuleFilterProps> {
  @observable private _Modules: Module[];
  @observable private _filter: ModuleFilterProps;
  @observable private _loading: boolean;
  @observable private _limit: number;
  @observable private _offset: number;
  @observable private _total: number;

  @action private _endLoading = endLoading(200).bind(this);

  public constructor(services: Services) {
    super(services);

    this._Modules = [];
    this._filter = {};
    this._loading = false;
    this._limit = 20;
    this._offset = 0;
    this._total = 0;
  }

  @action public load: LoadModule = async params => {
    this._loading = true;

    let provider: number | null = null;
    const { _filter, _limit, _offset } = this;

    const filter = (params && params.filter) || toJS(_filter);
    const limit = (params && params.limit) || _limit;
    const offset = (params && params.offset) || _offset;

    this._filter = filter;
    this._limit = limit;
    this._offset = offset;

    await this._services.opencity.requests
      .organizationModuleIndex({
        params: {
          filter,
          limit,
          offset,
        },
      })
      .then(({ data: { result } }: AxiosResponse<ModuleIndexResponse>) => {
        if (result && Array.isArray(result.items)) {
          if (result.items.length > 0 && result.items[0] && result.items[0].providerId) {
            provider = result.items[0].providerId;
          }
        }
      })
      .finally(this._endLoading);

    return provider;
  };

  @action public cleanUp = (): void => {
    this._Modules = [];
    this._filter = {};
    this._loading = false;
    this._limit = 20;
    this._offset = 0;
    this._total = 0;
  };

  @computed public get list(): Module[] {
    return toJS(this._Modules);
  }

  @computed public get filter(): ModuleFilterProps {
    return toJS(this._filter);
  }

  @computed public get loading(): boolean {
    return this._loading;
  }

  @computed public get limit(): number {
    return this._limit;
  }

  @computed public get offset(): number {
    return this._offset;
  }

  @computed public get total(): number {
    return this._total;
  }
}

export default ModuleStore;
